import { IKimSendPayload, IKimSendResponse, IVzdSearchParams, IVzdSearchResult } from '@alberta/konexi-shared';
import { Injectable } from '@angular/core';
import { SessionController } from 'src/app/auth/session/session-controller';
import { EnvironmentService } from 'src/app/shared/services/environment/environment.service';
import { NetworkInfoService } from 'src/app/shared/services/network/network-info.service';

/** The directory service could not be queried: offline, network error or any error response. */
export class VzdUnavailableError extends Error {
  constructor(message = 'VZD unavailable') {
    super(message);
    this.name = 'VzdUnavailableError';
  }
}

export class KimSendError extends Error {
  constructor(
    public readonly status: number | null,
    message: string
  ) {
    super(message);
    this.name = 'KimSendError';
  }

  /** Netzwerkfehler oder Gateway-Fehler – ein erneuter Versuch kann helfen. */
  public get isRetriable(): boolean {
    return this.status === null || this.status >= 500 || this.status === 502;
  }
}

/**
 * HTTP-Client für den KIM-Microservice (ti.services kim.service).
 */
@Injectable({ providedIn: 'root' })
export class KimApiService {
  constructor(
    private _session: SessionController,
    private _environmentService: EnvironmentService,
    private _networkInfoService: NetworkInfoService
  ) {}

  public async searchVzd(params: IVzdSearchParams): Promise<IVzdSearchResult> {
    // Bail out before buildHeaders(): there is no point in asking for an access token offline.
    if (this.isOffline) {
      throw new VzdUnavailableError('device is offline');
    }

    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null && `${value}`.trim() !== '') {
        query.set(key, `${value}`);
      }
    }

    let response: Response;
    try {
      response = await fetch(`${this._environmentService.kimServiceUrl}/v1/vzd/search?${query.toString()}`, {
        method: 'GET',
        headers: await this.buildHeaders(),
      });
    } catch (error) {
      throw new VzdUnavailableError(error?.message);
    }

    if (!response.ok) {
      // Every failed search means the same thing to the user: the directory cannot be reached
      // right now. Log the real status so the cause stays visible, but let the UI show the
      // retryable info box instead of an empty result list.
      window.logger.error('KIM VZD search failed', new Error(`VZD search failed with status ${response.status}`));
      throw new VzdUnavailableError(`VZD search failed with status ${response.status}`);
    }

    return response.json();
  }

  public async send(payload: IKimSendPayload): Promise<IKimSendResponse> {
    if (this.isOffline) {
      // Same reasoning as in searchVzd: never ask for a token that cannot be refreshed.
      throw new KimSendError(null, 'device is offline');
    }

    let response: Response;
    try {
      response = await fetch(`${this._environmentService.kimServiceUrl}/v1/send`, {
        method: 'POST',
        headers: await this.buildHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload),
      });
    } catch (error) {
      throw new KimSendError(null, error?.message ?? 'network error');
    }

    if (!response.ok) {
      throw new KimSendError(response.status, `KIM send failed with status ${response.status}`);
    }

    return response.json();
  }

  private get isOffline(): boolean {
    return this._networkInfoService.isNotConnected || navigator.onLine === false;
  }

  private async buildHeaders(additionalHeaders: Record<string, string> = {}): Promise<Record<string, string>> {
    const accessToken = await this._session.getAccessToken();
    return {
      Authorization: `Bearer ${accessToken}`,
      ...additionalHeaders,
    };
  }
}
